import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';

import { corLegivelSobreBranco, lerCor, paraCss, textoSobre } from './contraste-tema';

/**
 * Pergunta, depois do código validado, se o usuário quer confiar neste dispositivo.
 *
 * Só um dispositivo fica confiável por usuário: confiar neste revoga o anterior no backend, e o
 * texto avisa isso. Esc e "Agora não" seguem sem confiar.
 */
@Component({
  selector: 'app-confiar-dispositivo',
  templateUrl: './confiar-dispositivo.component.html',
  styleUrls: ['./confiar-dispositivo.component.css']
})
export class ConfiarDispositivoComponent implements OnInit, AfterViewInit {
  /** Prazo do credor, em dias. */
  @Input() dias = 30;

  /** Enquanto o backend grava: trava os botões. */
  @Input() processando = false;

  @Output() confirmar = new EventEmitter<void>();
  @Output() recusar = new EventEmitter<void>();

  @ViewChild('botaoConfiar') botaoConfiar: ElementRef<HTMLButtonElement>;
  @ViewChild('dialogo') dialogo: ElementRef<HTMLElement>;

  readonly dispositivo = ConfiarDispositivoComponent.descreverDispositivo(navigator.userAgent);

  constructor(private elemento: ElementRef<HTMLElement>) { }

  get rotuloDias(): string {
    return `${this.dias} ${this.dias === 1 ? 'dia' : 'dias'}`;
  }

  ngOnInit(): void {
    this.aplicarContrasteDoTema();
  }

  /**
   * A --primary é a do sistema ou a corPrimaria do white label. Título e botão usam versões dela
   * que leem sobre branco; o texto do botão respeita corTextoMenuLateral quando o credor define.
   */
  private aplicarContrasteDoTema(): void {
    const host = this.elemento.nativeElement;
    const estilo = getComputedStyle(host);
    const primaria = lerCor(estilo.getPropertyValue('--primary'));
    if (!primaria)
      return;

    host.style.setProperty('--confiar-destaque', paraCss(corLegivelSobreBranco(primaria)));

    if (!estilo.getPropertyValue('--corTextoMenuLateral').trim())
      host.style.setProperty('--confiar-texto-botao', textoSobre(primaria, 3));
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.botaoConfiar?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape')
  aoPressionarEsc(): void {
    if (!this.processando)
      this.recusar.emit();
  }

  /** Mantém o Tab dentro do diálogo: o cartão do código continua atrás e não pode receber foco. */
  manterFoco(evento: KeyboardEvent): void {
    if (evento.key !== 'Tab')
      return;

    const focaveis = Array.from(this.dialogo.nativeElement.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    if (focaveis.length === 0)
      return;

    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    if (evento.shiftKey && document.activeElement === primeiro) {
      ultimo.focus();
      evento.preventDefault();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      primeiro.focus();
      evento.preventDefault();
    }
  }

  /** "Chrome no Windows". O mesmo user agent vai para o backend como descrição do dispositivo. */
  static descreverDispositivo(userAgent: string): { descricao: string; movel: boolean } {
    const ua = userAgent || '';

    const navegador =
      /Edg\//.test(ua) ? 'Edge' :
      /OPR\/|Opera/.test(ua) ? 'Opera' :
      /Firefox\//.test(ua) ? 'Firefox' :
      /SamsungBrowser\//.test(ua) ? 'Samsung Internet' :
      /Chrome\//.test(ua) ? 'Chrome' :
      /Safari\//.test(ua) ? 'Safari' :
      'Navegador';

    const sistema =
      /Android/.test(ua) ? 'Android' :
      /iPhone|iPod/.test(ua) ? 'iPhone' :
      /iPad/.test(ua) ? 'iPad' :
      /Windows/.test(ua) ? 'Windows' :
      /Mac OS X|Macintosh/.test(ua) ? 'Mac' :
      /Linux/.test(ua) ? 'Linux' :
      null;

    return {
      descricao: sistema ? `${navegador} no ${sistema}` : navegador,
      movel: /Android|iPhone|iPod|iPad|Mobile/.test(ua)
    };
  }
}
